import { useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollRevealOptions = {}
) {
  const { threshold = 0.15, rootMargin = '0px 0px -60px 0px', once = true } = options;
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}

export function revealClass(isVisible: boolean, delay = 0): string {
  const delayClass =
    delay === 0 ? '' :
    delay === 100 ? 'delay-100' :
    delay === 150 ? 'delay-150' :
    delay === 200 ? 'delay-200' :
    delay === 300 ? 'delay-300' :
    delay === 400 ? 'delay-400' :
    delay === 500 ? 'delay-500' : '';

  return `transition-all duration-700 ease-out ${delayClass} ${
    isVisible
      ? 'opacity-100 translate-y-0'
      : 'opacity-0 translate-y-8'
  }`;
}
